import React, { useState } from 'react';
import { Check, X, Power, PowerOff, Edit } from 'lucide-react';
import { useMarkup } from '../contexts/MarkupContext';

interface MarkupBulkActionsProps {
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

const MarkupBulkActions: React.FC<MarkupBulkActionsProps> = ({
  selectedIds,
  onSelectionChange,
}) => {
  const { state, actions } = useMarkup();
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    type: '',
    target: '',
    is_active: '',
  });

  const selectedMarkups = state.markups.filter(markup => selectedIds.includes(markup.id));

  const handleBulkActivate = async () => {
    if (!window.confirm(`Ativar ${selectedIds.length} markup(s) selecionado(s)?`)) {
      return;
    }

    try {
      for (const id of selectedIds) {
        await actions.updateMarkup(id, { is_active: true });
      }
      onSelectionChange([]);
    } catch (error) {
      console.error('Erro na ativação em lote:', error);
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Desativar ${selectedIds.length} markup(s) selecionado(s)?`)) {
      return;
    }

    try {
      for (const id of selectedIds) {
        await actions.updateMarkup(id, { is_active: false });
      }
      onSelectionChange([]);
    } catch (error) {
      console.error('Erro na desativação em lote:', error);
    }
  };

  const handleBulkEdit = async () => {
    if (!window.confirm(`Aplicar mudanças a ${selectedIds.length} markup(s) selecionado(s)?`)) {
      return;
    }

    const updateData: any = {};
    
    if (bulkEditData.type) updateData.type = bulkEditData.type;
    if (bulkEditData.target) updateData.target = bulkEditData.target;
    if (bulkEditData.is_active !== '') updateData.is_active = bulkEditData.is_active === 'true';

    if (Object.keys(updateData).length === 0) {
      alert('Selecione pelo menos um campo para atualizar');
      return;
    }

    try {
      for (const id of selectedIds) {
        await actions.updateMarkup(id, updateData);
      }
      setShowBulkEdit(false);
      setBulkEditData({ type: '', target: '', is_active: '' });
      onSelectionChange([]);
    } catch (error) {
      console.error('Erro na edição em lote:', error);
    }
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <div className="card" style={{ 
      marginBottom: '20px',
      background: 'var(--color-lightblue)',
      border: '1px solid var(--color-blue)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <strong>{selectedIds.length} markup(s) selecionado(s)</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: 'var(--font-size-14)', color: 'var(--color-darkgray)' }}>
            {selectedMarkups.filter(m => m.is_active).length} ativo(s), {selectedMarkups.filter(m => !m.is_active).length} inativo(s)
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleBulkActivate}
            className="btn btn-success"
            style={{ padding: '6px 12px' }}
            title="Ativar selecionados"
          >
            <Power size={14} />
            Ativar
          </button>

          <button
            onClick={handleBulkDeactivate}
            className="btn btn-danger"
            style={{ padding: '6px 12px' }}
            title="Desativar selecionados"
          >
            <PowerOff size={14} />
            Desativar
          </button>

          <button
            onClick={() => setShowBulkEdit(!showBulkEdit)}
            className="btn btn-secondary"
            style={{ padding: '6px 12px' }}
            title="Edição em lote"
          >
            <Edit size={14} />
            Editar
          </button>

          <button
            onClick={() => onSelectionChange([])}
            className="btn btn-outline"
            style={{ padding: '6px 12px' }}
            title="Limpar seleção"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {showBulkEdit && (
        <div style={{ 
          marginTop: '16px', 
          padding: '16px', 
          background: 'white', 
          borderRadius: 'var(--br-5)',
          border: '1px solid var(--color-lightgray)'
        }}>
          <h4 style={{ margin: '0 0 16px 0' }}>Edição em Lote</h4>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div className="form-group">
              <label className="form-label">Tipo (deixe vazio para não alterar)</label>
              <select
                value={bulkEditData.type}
                onChange={(e) => setBulkEditData({ ...bulkEditData, type: e.target.value })}
                className="form-select"
              >
                <option value="">Não alterar</option>
                <option value="percentage">Percentual</option>
                <option value="fixed_value">Valor Fixo</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Aplicado em (deixe vazio para não alterar)</label>
              <select
                value={bulkEditData.target}
                onChange={(e) => setBulkEditData({ ...bulkEditData, target: e.target.value })}
                className="form-select"
              >
                <option value="">Não alterar</option>
                <option value="adult_price">Preço Adulto</option>
                <option value="boarding_tax">Taxa de Embarque</option>
                <option value="total_price">Preço Total</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status (deixe vazio para não alterar)</label>
              <select
                value={bulkEditData.is_active}
                onChange={(e) => setBulkEditData({ ...bulkEditData, is_active: e.target.value })}
                className="form-select"
              >
                <option value="">Não alterar</option>
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleBulkEdit}
              className="btn btn-primary"
              style={{ padding: '8px 16px' }}
            >
              <Check size={14} />
              Aplicar Mudanças
            </button>
            
            <button
              onClick={() => setShowBulkEdit(false)}
              className="btn btn-secondary"
              style={{ padding: '8px 16px' }}
            >
              <X size={14} />
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkupBulkActions;